/**
 * Merchants: the verbs, and the scarcity that makes them worth anything.
 *
 * A crown has between two and six of these for sixteen markets. That ratio is
 * the design. Everything else in this system is arithmetic that could be a
 * readout — value flows, control accrues, income lands — and the only reason it
 * is a *game* is that the player has a handful of interventions and has to
 * choose where to spend them.
 *
 * Two things a merchant may be told to do, and they are genuinely different
 * decisions rather than two settings of one dial:
 *
 * - **Collect.** More of this node's value stays here, and you take your control
 *   share of it. Money this month, in a place you already hold. The safe answer,
 *   and usually the right one for a crown with one merchant.
 * - **Steer.** You take nothing here. You lean on what leaves so that more of it
 *   goes down one particular road — toward a market you dominate, where you are
 *   presumably collecting. Money in two months, more of it, and only if you hold
 *   the far end. This is "push trade", and it is the whole reason the graph has
 *   arrows instead of buckets.
 *
 * The verbs are `postMerchant` and `recallMerchant`, and both of them are shared
 * by the player's commands and the AI council. Nobody gets a second door.
 *
 * Every function that can refuse has a `…Reason` twin returning a sentence, on
 * the pattern `diploacts.ts` set: the world map greys the button and says why,
 * and the command itself refuses politely as a second line of defence.
 */

import type { CampaignState } from './types.ts';
import {
  MERCHANTS_BASE,
  MERCHANTS_MAX,
  MERCHANTS_PER_PROVINCES,
  MERCHANT_HOPS_PER_MONTH,
  MERCHANT_TRAVEL_MAX,
  MerchantMission,
  type Merchant,
} from './tradetypes.ts';
import { canReachNode, downstreamOf, nodeName, tradeNodeOf } from './trade.ts';
import { hopsFrom } from './graph.ts';
import { logLine, nationName } from './state.ts';

/**
 * How many merchants this crown has, all told.
 *
 * Provinces, not gold, and not a building. Gold would make merchants something a
 * rich crown simply buys more of, which is the snowball this layer already
 * spends `UNREST_STRAIN` fighting; a building would put the strategic layer's
 * most interesting decision behind the settlement layer, where the AI cannot
 * follow. Ground held is the one currency every crown on the board spends in the
 * same way.
 */
export function merchantCap(c: CampaignState, nation: number): number {
  let held = 0;
  for (const p of c.provinces) if (p.owner === nation) held++;
  return Math.min(MERCHANTS_MAX, MERCHANTS_BASE + Math.floor(held / MERCHANTS_PER_PROVINCES));
}

export function merchantsOf(c: CampaignState, nation: number): Merchant[] {
  return c.trade.merchants.filter((m) => m.nation === nation);
}

export function merchantAt(
  c: CampaignState,
  nation: number,
  node: number,
): Merchant | undefined {
  return c.trade.merchants.find((m) => m.nation === nation && m.node === node);
}

/** Merchants not currently posted anywhere. */
export function freeMerchants(c: CampaignState, nation: number): number {
  return Math.max(0, merchantCap(c, nation) - merchantsOf(c, nation).length);
}

/**
 * Months on the road to reach a node, from the crown's nearest holding.
 *
 * Counted in province hops rather than node hops, because that is the distance
 * the map actually shows and the number a player can predict by looking at it.
 * A merchant sent into the node next door is there in a month; one sent across
 * the realm takes the better part of a season, and in the meantime it is doing
 * nothing at all for anybody.
 */
export function travelMonths(c: CampaignState, nation: number, node: number): number {
  const seat = c.trade.nodes[node]?.seat ?? -1;
  if (seat < 0) return 1;
  const dist = hopsFrom(c, seat);
  let best = -1;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    const d = dist[p];
    if (d < 0) continue;
    if (best < 0 || d < best) best = d;
  }
  if (best < 0) return MERCHANT_TRAVEL_MAX;
  return Math.min(MERCHANT_TRAVEL_MAX, 1 + Math.floor(best / MERCHANT_HOPS_PER_MONTH));
}

// ── Legality ───────────────────────────────────────────────────────────────

/** '' when the posting is legal; otherwise the sentence the panel should show. */
export function postReason(
  c: CampaignState,
  nation: number,
  node: number,
  mission: number,
  toward: number,
): string {
  const t = c.trade;
  if (!t || node < 0 || node >= t.nodes.length) return 'There is no market there.';
  if (nation < 0 || nation >= c.nations.length || !c.nations[nation].alive) {
    return 'There is no crown to send one.';
  }
  if (!canReachNode(c, nation, node)) {
    return `No road of ours runs to ${nodeName(c, node)}.`;
  }
  if (mission === MerchantMission.Steer) {
    if (!downstreamOf(c, node).includes(toward)) {
      return `Nothing runs from ${nodeName(c, node)} to ${nodeName(c, toward)}.`;
    }
  }
  // Retasking somebody who is already there is always allowed; only a *new*
  // posting needs a merchant to spare.
  if (!merchantAt(c, nation, node) && freeMerchants(c, nation) <= 0) {
    return 'Every merchant we have is already somewhere.';
  }
  return '';
}

export function canPost(
  c: CampaignState,
  nation: number,
  node: number,
  mission: number,
  toward: number,
): boolean {
  return postReason(c, nation, node, mission, toward) === '';
}

// ── The verbs ──────────────────────────────────────────────────────────────

/**
 * Post a merchant, or retask the one already standing there.
 *
 * Retasking costs no travel — the man is in the room, he is simply told
 * something else. Moving one from another node is not a thing this does: recall
 * it first, which costs the month it is standing idle. That asymmetry is
 * deliberate and is what stops the optimal play being "re-solve the whole
 * network every month for free".
 */
export function postMerchant(
  c: CampaignState,
  nation: number,
  node: number,
  mission: number,
  toward: number,
): Merchant | undefined {
  if (!canPost(c, nation, node, mission, toward)) return undefined;
  const want = mission === MerchantMission.Steer ? MerchantMission.Steer : MerchantMission.Collect;
  const aim = want === MerchantMission.Steer ? toward : -1;

  const sitting = merchantAt(c, nation, node);
  if (sitting) {
    sitting.mission = want;
    sitting.toward = aim;
    return sitting;
  }

  const m: Merchant = {
    id: c.trade.nextMerchantId++,
    nation,
    node,
    mission: want,
    toward: aim,
    travel: travelMonths(c, nation, node),
  };
  c.trade.merchants.push(m);
  // Ordered by nation then node so iteration is a fact about the board rather
  // than about the order somebody happened to click.
  c.trade.merchants.sort((a, b) => a.nation - b.nation || a.node - b.node || a.id - b.id);
  return m;
}

/** Take a merchant off a node. It is available again immediately. */
export function recallMerchant(c: CampaignState, nation: number, node: number): boolean {
  const i = c.trade.merchants.findIndex((m) => m.nation === nation && m.node === node);
  if (i < 0) return false;
  c.trade.merchants.splice(i, 1);
  return true;
}

/**
 * Walk the road one month.
 *
 * Also the place a posting is quietly cancelled: a merchant sent to a market its
 * crown can no longer reach — the province it was sent from having been lost
 * while he walked — comes home rather than standing in a foreign square being
 * counted as trade power forever.
 */
export function advanceMerchants(c: CampaignState): void {
  const t = c.trade;
  const arrived: Merchant[] = [];
  for (const m of t.merchants) {
    if (m.travel > 0) {
      m.travel--;
      if (m.travel <= 0) arrived.push(m);
    }
  }

  const gone = t.merchants.filter((m) => !canReachNode(c, m.nation, m.node));
  for (const m of gone) recallMerchant(c, m.nation, m.node);

  // Over the cap — the crown lost the provinces that paid for them. The furthest
  // from home goes first, which is both the least useful and the easiest to
  // explain.
  for (let n = 0; n < c.nations.length; n++) {
    let over = merchantsOf(c, n).length - merchantCap(c, n);
    while (over > 0) {
      const mine = merchantsOf(c, n);
      if (mine.length === 0) break;
      let worst = mine[0];
      for (const m of mine) if (m.travel > worst.travel || (m.travel === worst.travel && m.node > worst.node)) worst = m;
      recallMerchant(c, n, worst.node);
      if (n === c.playerNation) {
        logLine(c, `The ${nodeName(c, worst.node)} factor is called home. We cannot keep him.`);
      }
      over--;
    }
  }

  for (const m of arrived) {
    if (m.nation !== c.playerNation) continue;
    if (!t.merchants.includes(m)) continue;
    logLine(
      c,
      m.mission === MerchantMission.Collect
        ? `Our factor reaches ${nodeName(c, m.node)} and opens the ledger.`
        : `Our factor reaches ${nodeName(c, m.node)} and starts turning the carts toward ` +
            `${nodeName(c, m.toward)}.`,
    );
  }
}

/** One line for the chronicle, when a crown first takes a market off another. */
export function logMarketTurnover(
  c: CampaignState,
  node: number,
  from: number,
  to: number,
): void {
  if (from === to) return;
  if (from !== c.playerNation && to !== c.playerNation) return;
  logLine(
    c,
    `${nationName(c, to)} now has the whip hand at ${nodeName(c, node)}` +
      (from >= 0 ? `, over ${nationName(c, from)}.` : '.'),
  );
}

/** The node a province trades through, re-exported so the UI needs one import. */
export { tradeNodeOf };
